import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, ShoppingBag, MapPin, LogOut, ChevronRight, Package, Clock, CheckCircle, XCircle, Truck, Trash2, Star, Plus, Pencil } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import AddressFormModal from '../../components/common/AddressFormModal';
import { cldImg } from '../../utils/cloudinary';

const STATUS_CONFIG = {
  pending:          { label: 'Pending',           color: 'bg-yellow-100 text-yellow-700',  icon: Clock },
  confirmed:        { label: 'Confirmed',          color: 'bg-blue-100 text-blue-700',      icon: CheckCircle },
  packed:           { label: 'Packed',             color: 'bg-purple-100 text-purple-700',  icon: Package },
  shipped:          { label: 'Shipped',            color: 'bg-indigo-100 text-indigo-700',  icon: Truck },
  out_for_delivery: { label: 'Out for Delivery',   color: 'bg-orange-100 text-orange-700',  icon: Truck },
  delivered:        { label: 'Delivered',          color: 'bg-green-100 text-green-700',    icon: CheckCircle },
  cancelled:        { label: 'Cancelled',          color: 'bg-red-100 text-red-700',        icon: XCircle },
  refunded:         { label: 'Refunded',           color: 'bg-gray-100 text-gray-600',      icon: XCircle },
};

function OrderCard({ order }) {
  const [open, setOpen] = useState(false);
  const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  const date = new Date(order.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <button className="w-full text-left p-4 flex items-center gap-3" onClick={() => setOpen(o => !o)}>
        <div className="w-10 h-10 bg-brand-yellow-light rounded-xl flex items-center justify-center flex-shrink-0">
          <Package size={18} className="text-brand-yellow" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-brand-dark text-sm">{order.order_number}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>
              <Icon size={10} className="inline mr-1" />{cfg.label}
            </span>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">{date} · ₹{Number(order.total).toLocaleString('en-IN')}</p>
        </div>
        <ChevronRight size={18} className={`text-gray-400 transition-transform flex-shrink-0 ${open ? 'rotate-90' : ''}`} />
      </button>

      {open && (
        <div className="border-t border-gray-50 px-4 pb-4">
          {/* Items */}
          <div className="space-y-3 mt-3">
            {order.items?.map(item => (
              <div key={item.id} className="flex gap-3 items-center">
                <div className="w-12 h-12 rounded-lg bg-gray-50 overflow-hidden flex-shrink-0">
                  {item.snapshot?.image
                    ? <img src={cldImg(item.snapshot.image, 120)} alt={item.snapshot.name} className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center text-xl">🛍️</div>
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-brand-dark line-clamp-1">{item.snapshot?.name}</p>
                  {(item.snapshot?.color || item.snapshot?.size) && (
                    <p className="text-xs text-gray-400">{[item.snapshot.color, item.snapshot.size].filter(Boolean).join(' / ')}</p>
                  )}
                  <p className="text-xs text-gray-400">Qty: {item.quantity} · ₹{Number(item.price).toLocaleString('en-IN')}</p>
                </div>
                {item.snapshot?.slug && (
                  <Link to={`/product/${item.snapshot.slug}`} className="text-xs text-brand-yellow hover:underline flex-shrink-0">
                    View
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="mt-4 bg-gray-50 rounded-xl p-3 text-xs space-y-1.5">
            <div className="flex justify-between text-gray-500">
              <span>Order ID</span>
              <span className="font-semibold text-brand-dark select-all">{order.order_number}</span>
            </div>
            <div className="flex justify-between text-gray-500">
              <span>Subtotal</span><span>₹{Number(order.subtotal).toLocaleString('en-IN')}</span>
            </div>
            {Number(order.discount) > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Discount {order.coupon_code && `(${order.coupon_code})`}</span>
                <span>- ₹{Number(order.discount).toLocaleString('en-IN')}</span>
              </div>
            )}
            <div className="flex justify-between text-gray-500">
              <span>Shipping</span>
              <span>{Number(order.shipping_charge) === 0 ? 'FREE' : `₹${Number(order.shipping_charge).toLocaleString('en-IN')}`}</span>
            </div>
            <div className="flex justify-between font-semibold text-brand-dark pt-1 border-t border-gray-200">
              <span>Total</span><span>₹{Number(order.total).toLocaleString('en-IN')}</span>
            </div>
          </div>

          {/* Courier + tracking number */}
          {order.tracking_number && (
            <div className="mt-3 text-xs text-gray-500">
              {order.courier_name && (
                <span className="mr-3">Courier: <span className="font-semibold text-brand-dark">{order.courier_name}</span></span>
              )}
              Tracking: <span className="font-semibold text-brand-dark">{order.tracking_number}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProfileTab({ user, onSave }) {
  const [name, setName] = useState(user.name || '');
  const [email, setEmail] = useState(user.email || '');
  const [mobile, setMobile] = useState(user.mobile || user.phone || '');
  const [saving, setSaving] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Full name is required'); return; }
    if (!email.trim() || !emailRegex.test(email.trim())) { toast.error('Enter a valid email address'); return; }

    const emailChanged = email.trim().toLowerCase() !== user.email.toLowerCase();

    if (emailChanged) {
      // Send OTP to new email first
      setSaving(true);
      try {
        await api.post('/auth/send-email-otp', { newEmail: email.trim() });
        toast.success('OTP sent to ' + email.trim());
        setShowOtp(true);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to send OTP');
      } finally {
        setSaving(false);
      }
      return;
    }

    // Email not changed — save directly
    setSaving(true);
    try {
      await api.put('/auth/profile', { name: name.trim(), email: email.trim(), mobile });
      onSave({ name: name.trim(), email: email.trim(), mobile });
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim() || otp.length !== 6) { toast.error('Enter the 6-digit OTP'); return; }
    setOtpLoading(true);
    try {
      await api.put('/auth/verify-email-otp', { otp: otp.trim(), name: name.trim(), mobile });
      onSave({ name: name.trim(), email: email.trim(), mobile });
      toast.success('Email verified and profile updated');
      setShowOtp(false);
      setOtp('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  if (showOtp) {
    return (
      <div className="space-y-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm text-gray-700">
          OTP sent to <strong>{email}</strong>. Enter it below to verify your new email.
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Enter OTP</label>
          <input
            value={otp}
            onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-yellow tracking-widest text-center text-lg font-bold"
            placeholder="_ _ _ _ _ _"
            type="text"
            maxLength={6}
            inputMode="numeric"
          />
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleVerifyOtp}
            disabled={otpLoading}
            className="btn-primary px-6 py-3 rounded-xl font-semibold disabled:opacity-60 flex items-center gap-2"
          >
            {otpLoading && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            Verify & Save
          </button>
          <button
            onClick={() => { setShowOtp(false); setOtp(''); }}
            className="px-6 py-3 rounded-xl font-semibold border border-gray-200 text-sm text-gray-600"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name</label>
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-yellow"
          placeholder="Your name"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
        <input
          value={email}
          onChange={e => setEmail(e.target.value)}
          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-yellow"
          placeholder="your@email.com"
          type="email"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Mobile Number</label>
        <input
          value={mobile}
          onChange={e => setMobile(e.target.value)}
          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-yellow"
          placeholder="10-digit mobile number"
          type="tel"
          maxLength={15}
        />
      </div>
      <button
        onClick={handleSave}
        disabled={saving}
        className="btn-primary px-6 py-3 rounded-xl font-semibold disabled:opacity-60 flex items-center gap-2"
      >
        {saving && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
        Save Changes
      </button>
    </div>
  );
}

const TABS = [
  { id: 'orders', label: 'My Orders', icon: ShoppingBag },
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'addresses', label: 'Addresses', icon: MapPin },
];

export default function AccountPage() {
  const { user, logout, login } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [addresses, setAddresses] = useState([]);
  const [addrLoading, setAddrLoading] = useState(false);
  const [addrModal, setAddrModal] = useState(null); // null = closed | {} = add | address = edit

  const fetchAddresses = useCallback(async () => {
    setAddrLoading(true);
    try {
      const { data } = await api.get('/addresses');
      setAddresses(data.addresses || []);
    } catch {}
    finally { setAddrLoading(false); }
  }, []);

  useEffect(() => {
    if (tab === 'addresses') fetchAddresses();
  }, [tab, fetchAddresses]);

  const handleDeleteAddress = async (id) => {
    try {
      await api.delete(`/addresses/${id}`);
      setAddresses(prev => prev.filter(a => a.id !== id));
      toast.success('Address removed');
    } catch { toast.error('Failed to remove address'); }
  };

  const handleSetDefault = async (id) => {
    try {
      await api.put(`/addresses/${id}`, { is_default: true });
      fetchAddresses();
      toast.success('Default address updated');
    } catch { toast.error('Failed to update address'); }
  };

  useEffect(() => {
    if (!user) { navigate('/login', { state: { from: '/account' } }); return; }
    fetchOrders();
  }, [user]);

  const fetchOrders = async () => {
    try {
      const { data } = await api.get('/orders/my');
      setOrders(data.orders || []);
    } catch {
      // silent
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out');
    navigate('/');
  };

  const handleProfileSave = (updated) => {
    const token = localStorage.getItem('yellove_token');
    login(token, { ...user, ...updated });
  };

  if (!user) return null;

  const avatar = (user.name || user.email || 'Y')[0].toUpperCase();

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 md:py-10">

      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="w-14 h-14 bg-brand-yellow rounded-full flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
          {avatar}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-brand-dark truncate">{user.name || 'Welcome!'}</h1>
          <p className="text-sm text-gray-400 truncate">{user.phone || user.mobile || user.email}</p>
        </div>
        <button onClick={handleLogout} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-brand-red transition-colors">
          <LogOut size={16} />
          <span className="hidden sm:block">Logout</span>
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-6">

        {/* Sidebar tabs (desktop) / horizontal scroll (mobile) */}
        <div className="md:w-52 flex-shrink-0">
          <div className="flex md:flex-col gap-2 overflow-x-auto pb-1 md:pb-0 scrollbar-hide">
            {TABS.map(t => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                    tab === t.id
                      ? 'bg-brand-yellow text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-100'
                  }`}
                >
                  <Icon size={16} />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab content */}
        <div className="flex-1 min-w-0">

          {/* Orders */}
          {tab === 'orders' && (
            <div>
              <h2 className="text-lg font-bold text-brand-dark mb-4">My Orders</h2>
              {ordersLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="bg-white rounded-2xl border border-gray-100 p-4 animate-pulse">
                      <div className="flex gap-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-xl" />
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-gray-100 rounded w-1/3" />
                          <div className="h-3 bg-gray-100 rounded w-1/4" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                  <div className="w-16 h-16 bg-brand-yellow-light rounded-full flex items-center justify-center mx-auto mb-4">
                    <ShoppingBag size={28} className="text-brand-yellow" />
                  </div>
                  <h3 className="font-semibold text-brand-dark mb-2">No orders yet</h3>
                  <p className="text-gray-400 text-sm mb-6">Start shopping and your orders will appear here</p>
                  <Link to="/products" className="btn-primary px-6 py-2.5 rounded-xl font-semibold text-sm">Shop Now</Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {orders.map(order => <OrderCard key={order.id} order={order} />)}
                </div>
              )}
            </div>
          )}

          {/* Profile */}
          {tab === 'profile' && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <h2 className="text-lg font-bold text-brand-dark mb-5">Edit Profile</h2>
              <ProfileTab user={user} onSave={handleProfileSave} />
            </div>
          )}

          {/* Addresses */}
          {tab === 'addresses' && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold text-brand-dark">Saved Addresses</h2>
                <button onClick={() => setAddrModal({})}
                  className="btn-primary px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-1.5">
                  <Plus size={16} /> Add Address
                </button>
              </div>
              {addrLoading ? (
                <div className="space-y-3">
                  {[1,2].map(i => <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />)}
                </div>
              ) : addresses.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <MapPin size={40} className="mx-auto mb-3 text-gray-200" />
                  <p className="text-sm">No saved addresses yet</p>
                  <button onClick={() => setAddrModal({})} className="mt-3 text-sm text-brand-yellow font-semibold hover:underline">
                    + Add your first address
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {addresses.map(addr => (
                    <div key={addr.id} className={`rounded-xl border-2 p-4 relative ${addr.is_default ? 'border-brand-yellow bg-brand-yellow-light' : 'border-gray-100'}`}>
                      {addr.is_default && (
                        <span className="absolute top-3 right-20 text-xs font-bold text-brand-yellow flex items-center gap-1">
                          <Star size={11} className="fill-brand-yellow" /> Default
                        </span>
                      )}
                      <div className="absolute top-3 right-3 flex items-center gap-2">
                        <button onClick={() => setAddrModal(addr)} className="text-gray-300 hover:text-brand-yellow transition-colors">
                          <Pencil size={15} />
                        </button>
                        <button onClick={() => handleDeleteAddress(addr.id)} className="text-gray-300 hover:text-red-500 transition-colors">
                          <Trash2 size={15} />
                        </button>
                      </div>
                      <p className="font-semibold text-brand-dark text-sm">{addr.name}</p>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {addr.address_line1}{addr.address_line2 ? `, ${addr.address_line2}` : ''}
                      </p>
                      <p className="text-sm text-gray-500">{addr.city}, {addr.state} – {addr.pincode}</p>
                      <p className="text-sm text-gray-400 mt-0.5">{addr.phone}{addr.email ? ` · ${addr.email}` : ''}</p>
                      {!addr.is_default && (
                        <button
                          onClick={() => handleSetDefault(addr.id)}
                          className="mt-2 text-xs text-brand-yellow font-semibold hover:underline"
                        >
                          Set as default
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {addrModal && (
        <AddressFormModal
          address={addrModal.id ? addrModal : null}
          onClose={() => setAddrModal(null)}
          onSaved={() => { setAddrModal(null); fetchAddresses(); }}
        />
      )}
    </div>
  );
}
